import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Loader, Table, Group } from '@mantine/core';
import Header from '../../../components/util/header';
import { useSelector } from 'react-redux';
import { AppState } from '../../../client-redux/reducers';
import { Language } from '../../../languages/languageHandler';
import { fetchEventMsgsList, IEventMsg } from '../../../../libraries/helpdesk/events/events';
import HelpdeskFilterBar, { HelpdeskFilterBarFieldsData } from '../../components/helpdesk-filterbar';
import PaginationComponent from '../../../components/pagination';
import { useFitTableRows } from '../../../components/util/fit-table-rows-hook';
import { formatDateTimeNSecLabel, formatDateToISO } from '../../../utils/date';
import { useStatusOperDialog, isZarphSuccessResponse } from '../../../context/status-oper-dialog';
import './styles.scss';

const HelpdeskEvents: React.FC = () => {
  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  const controller = useRef(new AbortController()).current;
  const location = useLocation();
  const region = useSelector<AppState, string>(state => state.login.user?.region || 'UTC');

  const [loading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [offset, setOffset] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState(1);
  const {
    limit = 10,
    setLimit,
    tableRef
  } = useFitTableRows({
    bufferRows: -2,
    minHeightForDynamic: 400
  });
  const queryParams = new URLSearchParams(location.search);

  const [eid /* setEid */] = useState<number | undefined>(
    queryParams.get('eid') ? parseInt(queryParams.get('eid')!) : undefined
  );
  const [list, setList] = useState<IEventMsg[]>([]);

  const [filterBarData, setFilterBarData] = useState<HelpdeskFilterBarFieldsData>();
  const { handleApiError } = useStatusOperDialog();

  useEffect(() => {
    if (filterBarData && limit) {
      loadEventsList();
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterBarData, limit, offset]);

  const handleFilterBarOnDataChange = (data: HelpdeskFilterBarFieldsData) => {
    setFilterBarData(data);
    changePage(1);
  };

  const loadEventsList = () => {
    if (!filterBarData || !limit) return;

    setLoading(true);
    setList([]);
    setTotalRows(0);

    fetchEventMsgsList(
      controller,
      offset,
      limit,
      true,
      Number(eid) || filterBarData?.eid,
      filterBarData?.eventCode,
      formatDateToISO(filterBarData?.beginDateTime ?? undefined),
      formatDateToISO(filterBarData?.endDateTime ?? undefined)
    )
      .then(response => {
        if (isZarphSuccessResponse(response, [9004])) {
          setList(response?.data?.events || []);
          setTotalRows(response?.data?.totalRows || 0);
        } else {
          console.error('Error response:', response);
          handleApiError(response, lang.ALERT_ERROR_FETCHING_DATA);
          setLoading(false);
        }
      })
      .catch(err => {
        // unexpected errors go here
        handleApiError(err, lang.ALERT_ERROR_FETCHING_DATA);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const changePage = (page: number) => {
    const newOffset = (page - 1) * limit;
    setCurrentPage(page);
    setOffset(newOffset);
  };

  const resultsSelect = (newValue: number) => {
    const newLimit = Number(newValue);
    setLimit(newLimit);
    setCurrentPage(1);
    setOffset(0);
  };

  return (
    <div className="helpdesk-events">
      <Header pageTitle={lang.EVENT_LOG} />

      <HelpdeskFilterBar
        onSearch={loadEventsList}
        onDataChange={handleFilterBarOnDataChange}
        fieldsUsage={{
          eid: true,
          eventCode: true,
          beginDateTime: true,
          endDateTime: true
        }}
      />

      <Table.ScrollContainer minWidth={500} ref={tableRef}>
        <Table striped highlightOnHover verticalSpacing="sm">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{'ID'}</Table.Th>
              <Table.Th>{'EID'}</Table.Th>
              <Table.Th>{lang.EVENT_CODE}</Table.Th>
              <Table.Th>{lang.EQUIPMENT_DESCRIPTION}</Table.Th>
              <Table.Th>{lang.EVENT_DATE}</Table.Th>
            </Table.Tr>
          </Table.Thead>

          <Table.Tbody>
            {loading ? (
              <Table.Tr>
                <Table.Td colSpan={5} style={{ textAlign: 'center' }}>
                  <Loader color="#016273" />
                </Table.Td>
              </Table.Tr>
            ) : !list.length ? (
              <Table.Tr>
                <Table.Td colSpan={5} style={{ textAlign: 'center' }}>
                  {lang.GLOBAL_NO_RESULTS}
                </Table.Td>
              </Table.Tr>
            ) : (
              list?.map((event, index) => (
                <Table.Tr key={index}>
                  <Table.Td>{event.id}</Table.Td>
                  <Table.Td>{event.eid}</Table.Td>
                  <Table.Td>{event.eventCode}</Table.Td>
                  <Table.Td>{event.eventDescription}</Table.Td>
                  <Table.Td>{formatDateTimeNSecLabel(event.dateTime, region)}</Table.Td>
                </Table.Tr>
              ))
            )}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      <Group>
        <PaginationComponent
          total={totalRows}
          itemsPerPage={limit}
          dynamicLimit={limit}
          currentPage={currentPage}
          onPageChange={changePage}
          onResultsChange={resultsSelect}
          offset={offset}
        />
      </Group>
    </div>
  );
};

export default HelpdeskEvents;
